<template>
  <div class="fixed top-0 left-0 z-20 w-full h-full bg-black flex items-center justify-center">
    <input ref="photoInput" type="file" accept="image/jpeg,image/png" capture="environment" class="hidden" @change="onPhotoSelected" @cancel="onPhotoCancelled">
    <div class="absolute bottom-0 left-0 z-10 flex justify-between w-full px-2 py-10">
      <div class="flex items-center -mx-1">
        <button type="button" :disabled="!can('cancel')" @click="dispatch('cancel')" class="h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-white bg-gray-600 rounded-md">{{ __("Cancel") }}</button>
        <button type="button" v-if="can('reset')" @click="dispatch('reset')" class="justify-center h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-coral-400 rounded-md bg-white">{{ __("Reset") }}</button>
      </div>
      <div class="flex items-center -mx-1">
        <button type="button" v-if="can('takePhoto')" @click="dispatch('takePhoto')" class="h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-white rounded-md bg-coral-400">{{ __("Take photo") }}</button>
        <button type="button" v-if="can('startRecording')" @click="dispatch('startRecording')" class="h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-white rounded-md bg-coral-400">{{ __("Record") }}</button>
        <button type="button" v-if="can('stopRecording')" @click="dispatch('stopRecording')" class="justify-center h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-white rounded-md bg-coral-400">{{ __("Stop") }}</button>
        <loading-button v-if="can('submit') || status==='submitting'" :loading="status === 'submitting'" @click="dispatch('submit')" class="justify-center h-10 mx-1 px-4 min-w-24 text-sm font-semibold text-white rounded-md bg-coral-400">{{ __("Upload") }}</loading-button>
      </div>
    </div>
    <video v-show="status !== 'tookPhoto'" controls autoplay playsinline ref="video" class="w-screen h-screen" @canplay="onCanPlay"></video>
    <img v-if="status === 'tookPhoto'" :src="photoPreviewUrl" class="max-w-full max-h-full" alt="">
  </div>
</template>

<script>
  import dlv from 'dlv';
  import LoadingButton from "./LoadingButton";

  function dispatch({state, event, payload}, machine) {
    dlv(machine, [state, event], () => {
      console.log(machine, state, event);
    })(payload);
  }

  export default {
    components: {LoadingButton},
    data() {
      return {
        status: 'idle',
        recorder: null,
        file: null,
        photoPreviewUrl: null,

        streaming: false,
        maxWidth: 1280,
        maxHeight: 720,
        machine: {
          idle: {
            cancel: () => {
              this.status = 'cancelled';
              this.cancel();
            },
            startRecording: () => {
              this.status = 'recording';
              this.startRecording();
            },
            takePhoto: () => {
              this.takePhoto();
            },
          },
          openingPhoto: {},
          tookPhoto: {
            cancel: () => {
              this.status = 'cancelled';
              this.cancel();
            },
            submit: () => {
              this.status = 'submitting';
              this.$emit('mediaReady', this.file, this.file.name);
              this.dispatch('reset');
            },
            reset: () => {
              this.status = 'idle';
              this.reset();
            }
          },
          recording: {
            cancel: () => {
              this.status = 'cancelled';
              this.cancel();
            },
            stopRecording: () => {
              this.status = 'stoppedRecording';
              this.stopRecording();
            },
          },
          stoppedRecording: {
            cancel: () => {
              this.status = 'cancelled';
              this.cancel();
            },
            submit: () => {
              this.status = 'submitting';
              this.$emit('mediaReady', this.file);
              this.dispatch('reset');
            },
            reset: () => {
              this.status = 'idle';
              this.reset();
            }
          },
          submitting: {},
        }
      }
    },

    mounted() {
      this.init();
      window.addEventListener("orientationchange", this.onOrientationChange);
    },

    beforeDestroy() {
      window.removeEventListener("orientationchange", this.onOrientationChange);
      this.stopPreview();
      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl);
      }
    },

    methods: {
      onOrientationChange() {
        if (this.status !== 'idle') return;
        this.stopPreview();
        this.init();
      },

      init() {
        this.adjustDimensions();
        navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {width: {max: this.maxWidth}, height: {max: this.maxHeight}, facingMode: {ideal: "environment"}, frameRate: {ideal: 30, max: 60}}
        })
          .then((stream) => {
            this.$refs['video'].srcObject = stream;
            this.$refs['video'].play();
          })
          .catch(function (err) {
            console.log("An error occurred: ", err);
          });
      },
      reset() {
        this.stopPreview();
        this.recorder = null;
        this.file = null;
        if (this.photoPreviewUrl) {
          URL.revokeObjectURL(this.photoPreviewUrl);
          this.photoPreviewUrl = null;
        }
        this.$refs.photoInput.value = '';
        this.streaming = false;
        this.init();
      },

      adjustDimensions() {
        if (screen.orientation.angle === 90) {
          this.maxWidth = 1280;
          this.maxHeight = 720;
        } else {
          this.maxHeight = 1280;
          this.maxWidth = 720;
        }

        this.streaming = false;
      },

      can(event) {
        return this.machine[this.status].hasOwnProperty(event);
      },

      onCanPlay(ev) {
        if (!this.streaming) {
          this.$refs.video.setAttribute('width', this.maxWidth);
          this.$refs.video.setAttribute('height', this.maxHeight);
          this.streaming = true;
        }
      },

      takePhoto() {
        this.status = 'openingPhoto';
        this.stopPreview();
        this.$refs.photoInput.click();
      },

      stopPreview() {
        const stream = this.$refs.video.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          this.$refs.video.srcObject = null;
        }
      },

      onPhotoCancelled() {
        this.status = 'idle';
        this.init();
      },

      onPhotoSelected(event) {
        const file = event.target.files[0];
        if (!file) {
          this.status = 'idle';
          this.init();
          return;
        }

        this.file = file;
        this.photoPreviewUrl = URL.createObjectURL(file);
        this.status = 'tookPhoto';
      },

      dispatch(event, payload) {
        dispatch(
          {state: this.status, event, payload},
          this.machine
        )
      },

      cancel() {
        if (this.recorder) {
          this.recorder.stopRecording();
        }
        this.$emit('cancel');
      },

      captureCamera(callback) {
        navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {width: {max: this.maxWidth}, height: {max: this.maxHeight}, facingMode: {ideal: "environment"}, frameRate: {ideal: 30, max: 60}}
        }).then(function (camera) {
          callback(camera);
        }).catch(function (error) {
          alert('Unable to capture your camera. Please check console logs.');
          console.error(error);
        });
      },

      startRecording() {
        let self = this;

        this.captureCamera(function (camera) {
          self.$refs['video'].muted = true;
          self.$refs['video'].volume = 0;
          self.$refs['video'].srcObject = camera;

          self.recorder = window.RecordRTC(camera, {
            type: 'video',
            mimeType: 'video/webm',
          });

          self.recorder.startRecording();
          self.recorder.camera = camera;
        });
      },

      stopRecording() {
        this.recorder.stopRecording(this.prepareRecording);
      },

      prepareRecording() {
        this.$refs['video'].src = this.$refs['video'].srcObject = null;
        this.$refs['video'].muted = false;
        this.$refs['video'].volume = 1;
        this.$refs['video'].src = URL.createObjectURL(this.recorder.getBlob());

        this.file = new File([this.recorder.getBlob()], 'video.webm', {
          type: 'video/webm'
        });

        this.recorder.camera.stop();
        this.recorder.destroy();
        this.recorder = null;
      }

    }
  };
</script>
